/**
 * Curated country calling-code list used by the contact form's phone fields.
 * Flags are generated from the ISO 3166-1 alpha-2 code (no need to store emoji).
 *
 * `nsnMin`/`nsnMax` = expected national-number length (digits, no country code).
 * `example` = a realistic-looking sample national number for the placeholder.
 * Countries without curated nsn/example fall back to a generic 4–12 digit range.
 */

export const isoToFlagEmoji = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '🌐';
  return iso2
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
};

export const DEFAULT_NSN_MIN = 4;
export const DEFAULT_NSN_MAX = 12;

export const COUNTRY_CODES = [
  { iso2: 'PH', name: 'Philippines', dialCode: '+63', nsnMin: 10, nsnMax: 10, example: '917 123 4567' },
  { iso2: 'GB', name: 'United Kingdom', dialCode: '+44', nsnMin: 9, nsnMax: 10, example: '7911 123456' },
  { iso2: 'US', name: 'United States', dialCode: '+1', nsnMin: 10, nsnMax: 10, example: '202 555 0123' },
  { iso2: 'CA', name: 'Canada', dialCode: '+1', nsnMin: 10, nsnMax: 10, example: '416 555 0123' },
  { iso2: 'PT', name: 'Portugal', dialCode: '+351', nsnMin: 9, nsnMax: 9, example: '912 345 678' },
  { iso2: 'JP', name: 'Japan', dialCode: '+81', nsnMin: 9, nsnMax: 10, example: '90 1234 5678' },
  { iso2: 'KR', name: 'South Korea', dialCode: '+82', nsnMin: 9, nsnMax: 10, example: '10 1234 5678' },
  { iso2: 'TH', name: 'Thailand', dialCode: '+66', nsnMin: 8, nsnMax: 9, example: '81 234 5678' },
  { iso2: 'VN', name: 'Vietnam', dialCode: '+84', nsnMin: 9, nsnMax: 10, example: '91 234 5678' },
  { iso2: 'CN', name: 'China', dialCode: '+86', nsnMin: 11, nsnMax: 11, example: '138 1234 5678' },
  { iso2: 'ES', name: 'Spain', dialCode: '+34', nsnMin: 9, nsnMax: 9, example: '612 345 678' },
  { iso2: 'FR', name: 'France', dialCode: '+33', nsnMin: 9, nsnMax: 9, example: '6 12 34 56 78' },
  { iso2: 'DE', name: 'Germany', dialCode: '+49', nsnMin: 10, nsnMax: 11, example: '151 23456789' },
  { iso2: 'IT', name: 'Italy', dialCode: '+39', nsnMin: 9, nsnMax: 10, example: '312 456 7890' },
  { iso2: 'RU', name: 'Russia', dialCode: '+7', nsnMin: 10, nsnMax: 10, example: '912 345 6789' },
  { iso2: 'ID', name: 'Indonesia', dialCode: '+62', nsnMin: 9, nsnMax: 12, example: '812 345 678' },
  { iso2: 'SA', name: 'Saudi Arabia', dialCode: '+966', nsnMin: 9, nsnMax: 9, example: '51 234 5678' },
  { iso2: 'IN', name: 'India', dialCode: '+91', nsnMin: 10, nsnMax: 10, example: '98123 45678' },
  { iso2: 'TR', name: 'Turkey', dialCode: '+90', nsnMin: 10, nsnMax: 10, example: '532 123 4567' },
  { iso2: 'MX', name: 'Mexico', dialCode: '+52', nsnMin: 10, nsnMax: 10, example: '55 1234 5678' },
  { iso2: 'BR', name: 'Brazil', dialCode: '+55', nsnMin: 10, nsnMax: 11, example: '21 91234 5678' },
  { iso2: 'AU', name: 'Australia', dialCode: '+61', nsnMin: 9, nsnMax: 9, example: '412 345 678' },
  { iso2: 'NZ', name: 'New Zealand', dialCode: '+64', nsnMin: 8, nsnMax: 9, example: '21 123 4567' },
  { iso2: 'NL', name: 'Netherlands', dialCode: '+31', nsnMin: 9, nsnMax: 9, example: '6 1234 5678' },
  { iso2: 'BE', name: 'Belgium', dialCode: '+32', nsnMin: 8, nsnMax: 9, example: '470 12 34 56' },
  { iso2: 'CH', name: 'Switzerland', dialCode: '+41', nsnMin: 9, nsnMax: 9, example: '78 123 45 67' },
  { iso2: 'AT', name: 'Austria', dialCode: '+43', nsnMin: 10, nsnMax: 11, example: '664 123456' },
  { iso2: 'SE', name: 'Sweden', dialCode: '+46', nsnMin: 9, nsnMax: 9, example: '70 123 45 67' },
  { iso2: 'NO', name: 'Norway', dialCode: '+47', nsnMin: 8, nsnMax: 8, example: '412 34 567' },
  { iso2: 'DK', name: 'Denmark', dialCode: '+45', nsnMin: 8, nsnMax: 8, example: '32 12 34 56' },
  { iso2: 'FI', name: 'Finland', dialCode: '+358', nsnMin: 9, nsnMax: 9, example: '41 234 5678' },
  { iso2: 'PL', name: 'Poland', dialCode: '+48', nsnMin: 9, nsnMax: 9, example: '512 345 678' },
  { iso2: 'GR', name: 'Greece', dialCode: '+30', nsnMin: 10, nsnMax: 10, example: '691 234 5678' },
  { iso2: 'IE', name: 'Ireland', dialCode: '+353', nsnMin: 9, nsnMax: 9, example: '85 123 4567' },
  { iso2: 'ZA', name: 'South Africa', dialCode: '+27', nsnMin: 9, nsnMax: 9, example: '71 123 4567' },
  { iso2: 'EG', name: 'Egypt', dialCode: '+20', nsnMin: 10, nsnMax: 10, example: '101 234 5678' },
  { iso2: 'NG', name: 'Nigeria', dialCode: '+234', nsnMin: 10, nsnMax: 10, example: '801 234 5678' },
  { iso2: 'KE', name: 'Kenya', dialCode: '+254', nsnMin: 9, nsnMax: 9, example: '712 345 678' },
  { iso2: 'PK', name: 'Pakistan', dialCode: '+92', nsnMin: 10, nsnMax: 10, example: '301 234 5678' },
  { iso2: 'BD', name: 'Bangladesh', dialCode: '+880', nsnMin: 10, nsnMax: 10, example: '171 234 5678' },
  { iso2: 'MY', name: 'Malaysia', dialCode: '+60', nsnMin: 9, nsnMax: 10, example: '12 345 6789' },
  { iso2: 'SG', name: 'Singapore', dialCode: '+65', nsnMin: 8, nsnMax: 8, example: '8123 4567' },
  { iso2: 'TW', name: 'Taiwan', dialCode: '+886', nsnMin: 9, nsnMax: 9, example: '912 345 678' },
  { iso2: 'HK', name: 'Hong Kong', dialCode: '+852', nsnMin: 8, nsnMax: 8, example: '5123 4567' },
  { iso2: 'UA', name: 'Ukraine', dialCode: '+380', nsnMin: 9, nsnMax: 9, example: '50 123 4567' },
  { iso2: 'RO', name: 'Romania', dialCode: '+40', nsnMin: 9, nsnMax: 9, example: '712 345 678' },
  { iso2: 'CZ', name: 'Czech Republic', dialCode: '+420', nsnMin: 9, nsnMax: 9, example: '601 123 456' },
  { iso2: 'HU', name: 'Hungary', dialCode: '+36', nsnMin: 9, nsnMax: 9, example: '20 123 4567' },
  { iso2: 'SK', name: 'Slovakia', dialCode: '+421', nsnMin: 9, nsnMax: 9, example: '912 345 678' },
  { iso2: 'BG', name: 'Bulgaria', dialCode: '+359', nsnMin: 8, nsnMax: 9, example: '87 123 4567' },
  { iso2: 'HR', name: 'Croatia', dialCode: '+385', nsnMin: 8, nsnMax: 9, example: '91 234 5678' },
  { iso2: 'SI', name: 'Slovenia', dialCode: '+386', nsnMin: 8, nsnMax: 8, example: '31 234 567' },
  { iso2: 'RS', name: 'Serbia', dialCode: '+381', nsnMin: 8, nsnMax: 9, example: '61 234 5678' },
  { iso2: 'LT', name: 'Lithuania', dialCode: '+370', nsnMin: 8, nsnMax: 8, example: '612 34567' },
  { iso2: 'LV', name: 'Latvia', dialCode: '+371', nsnMin: 8, nsnMax: 8, example: '21 234 567' },
  { iso2: 'EE', name: 'Estonia', dialCode: '+372', nsnMin: 7, nsnMax: 8, example: '5123 4567' },
  { iso2: 'IS', name: 'Iceland', dialCode: '+354', nsnMin: 7, nsnMax: 7, example: '611 2345' },
  { iso2: 'LU', name: 'Luxembourg', dialCode: '+352', nsnMin: 9, nsnMax: 9, example: '621 123 456' },
  { iso2: 'MT', name: 'Malta', dialCode: '+356', nsnMin: 8, nsnMax: 8, example: '9912 3456' },
  { iso2: 'CY', name: 'Cyprus', dialCode: '+357', nsnMin: 8, nsnMax: 8, example: '96 123456' },
  { iso2: 'AL', name: 'Albania', dialCode: '+355', nsnMin: 9, nsnMax: 9, example: '67 212 3456' },
  { iso2: 'MK', name: 'North Macedonia', dialCode: '+389', nsnMin: 8, nsnMax: 8, example: '70 123 456' },
  { iso2: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387', nsnMin: 8, nsnMax: 8, example: '61 123 456' },
  { iso2: 'ME', name: 'Montenegro', dialCode: '+382', nsnMin: 8, nsnMax: 8, example: '67 622 901' },
  { iso2: 'MD', name: 'Moldova', dialCode: '+373', nsnMin: 8, nsnMax: 8, example: '621 12345' },
  { iso2: 'GE', name: 'Georgia', dialCode: '+995', nsnMin: 9, nsnMax: 9, example: '555 123 456' },
  { iso2: 'AM', name: 'Armenia', dialCode: '+374', nsnMin: 8, nsnMax: 8, example: '77 123456' },
  { iso2: 'AZ', name: 'Azerbaijan', dialCode: '+994', nsnMin: 9, nsnMax: 9, example: '40 123 45 67' },
  { iso2: 'BY', name: 'Belarus', dialCode: '+375', nsnMin: 9, nsnMax: 9, example: '29 123 45 67' },
  { iso2: 'KZ', name: 'Kazakhstan', dialCode: '+7', nsnMin: 10, nsnMax: 10, example: '701 234 5678' },
  { iso2: 'UZ', name: 'Uzbekistan', dialCode: '+998', nsnMin: 9, nsnMax: 9, example: '90 123 4567' },
  { iso2: 'MN', name: 'Mongolia', dialCode: '+976', nsnMin: 8, nsnMax: 8, example: '8812 3456' },
  { iso2: 'NP', name: 'Nepal', dialCode: '+977', nsnMin: 10, nsnMax: 10, example: '984 123 4567' },
  { iso2: 'LK', name: 'Sri Lanka', dialCode: '+94', nsnMin: 9, nsnMax: 9, example: '71 234 5678' },
  { iso2: 'MM', name: 'Myanmar', dialCode: '+95', nsnMin: 8, nsnMax: 10, example: '9 212 345 67' },
  { iso2: 'KH', name: 'Cambodia', dialCode: '+855', nsnMin: 8, nsnMax: 9, example: '92 123 456' },
  { iso2: 'LA', name: 'Laos', dialCode: '+856', nsnMin: 9, nsnMax: 10, example: '20 123 4567' },
  { iso2: 'AE', name: 'United Arab Emirates', dialCode: '+971', nsnMin: 9, nsnMax: 9, example: '50 123 4567' },
  { iso2: 'QA', name: 'Qatar', dialCode: '+974', nsnMin: 8, nsnMax: 8, example: '3312 3456' },
  { iso2: 'KW', name: 'Kuwait', dialCode: '+965', nsnMin: 8, nsnMax: 8, example: '5012 3456' },
  { iso2: 'BH', name: 'Bahrain', dialCode: '+973', nsnMin: 8, nsnMax: 8, example: '3612 3456' },
  { iso2: 'OM', name: 'Oman', dialCode: '+968', nsnMin: 8, nsnMax: 8, example: '9212 3456' },
  { iso2: 'JO', name: 'Jordan', dialCode: '+962', nsnMin: 9, nsnMax: 9, example: '79 123 4567' },
  { iso2: 'LB', name: 'Lebanon', dialCode: '+961', nsnMin: 7, nsnMax: 8, example: '71 123 456' },
  { iso2: 'IQ', name: 'Iraq', dialCode: '+964', nsnMin: 10, nsnMax: 10, example: '790 123 4567' },
  { iso2: 'IR', name: 'Iran', dialCode: '+98', nsnMin: 10, nsnMax: 10, example: '912 345 6789' },
  { iso2: 'IL', name: 'Israel', dialCode: '+972', nsnMin: 9, nsnMax: 9, example: '50 123 4567' },
  { iso2: 'MA', name: 'Morocco', dialCode: '+212', nsnMin: 9, nsnMax: 9, example: '612 345 678' },
  { iso2: 'TN', name: 'Tunisia', dialCode: '+216', nsnMin: 8, nsnMax: 8, example: '20 123 456' },
  { iso2: 'DZ', name: 'Algeria', dialCode: '+213', nsnMin: 9, nsnMax: 9, example: '551 23 45 67' },
  { iso2: 'ET', name: 'Ethiopia', dialCode: '+251', nsnMin: 9, nsnMax: 9, example: '91 123 4567' },
  { iso2: 'GH', name: 'Ghana', dialCode: '+233', nsnMin: 9, nsnMax: 9, example: '24 123 4567' },
  { iso2: 'TZ', name: 'Tanzania', dialCode: '+255', nsnMin: 9, nsnMax: 9, example: '712 345 678' },
  { iso2: 'UG', name: 'Uganda', dialCode: '+256', nsnMin: 9, nsnMax: 9, example: '712 345 678' },
  { iso2: 'CI', name: "Côte d'Ivoire", dialCode: '+225', nsnMin: 10, nsnMax: 10, example: '01 23 45 67 89' },
  { iso2: 'SN', name: 'Senegal', dialCode: '+221', nsnMin: 9, nsnMax: 9, example: '77 123 45 67' },
  { iso2: 'CM', name: 'Cameroon', dialCode: '+237', nsnMin: 9, nsnMax: 9, example: '6 71 23 45 67' },
  { iso2: 'CO', name: 'Colombia', dialCode: '+57', nsnMin: 10, nsnMax: 10, example: '321 234 5678' },
  { iso2: 'VE', name: 'Venezuela', dialCode: '+58', nsnMin: 10, nsnMax: 10, example: '412 123 4567' },
  { iso2: 'PE', name: 'Peru', dialCode: '+51', nsnMin: 9, nsnMax: 9, example: '912 345 678' },
  { iso2: 'CL', name: 'Chile', dialCode: '+56', nsnMin: 9, nsnMax: 9, example: '9 1234 5678' },
  { iso2: 'EC', name: 'Ecuador', dialCode: '+593', nsnMin: 9, nsnMax: 9, example: '99 123 4567' },
  { iso2: 'BO', name: 'Bolivia', dialCode: '+591', nsnMin: 8, nsnMax: 8, example: '712 34567' },
  { iso2: 'PY', name: 'Paraguay', dialCode: '+595', nsnMin: 9, nsnMax: 9, example: '981 123 456' },
  { iso2: 'UY', name: 'Uruguay', dialCode: '+598', nsnMin: 8, nsnMax: 8, example: '94 123 456' },
  { iso2: 'AR', name: 'Argentina', dialCode: '+54', nsnMin: 10, nsnMax: 11, example: '9 11 2345 6789' },
  { iso2: 'PA', name: 'Panama', dialCode: '+507', nsnMin: 8, nsnMax: 8, example: '6123 4567' },
  { iso2: 'CR', name: 'Costa Rica', dialCode: '+506', nsnMin: 8, nsnMax: 8, example: '8312 3456' },
  { iso2: 'GT', name: 'Guatemala', dialCode: '+502', nsnMin: 8, nsnMax: 8, example: '5123 4567' },
  { iso2: 'HN', name: 'Honduras', dialCode: '+504', nsnMin: 8, nsnMax: 8, example: '9123 4567' },
  { iso2: 'SV', name: 'El Salvador', dialCode: '+503', nsnMin: 8, nsnMax: 8, example: '7012 3456' },
  { iso2: 'NI', name: 'Nicaragua', dialCode: '+505', nsnMin: 8, nsnMax: 8, example: '8123 4567' },
  { iso2: 'DO', name: 'Dominican Republic', dialCode: '+1', nsnMin: 10, nsnMax: 10, example: '809 123 4567' },
  { iso2: 'CU', name: 'Cuba', dialCode: '+53', nsnMin: 8, nsnMax: 8, example: '5123 4567' },
  { iso2: 'JM', name: 'Jamaica', dialCode: '+1', nsnMin: 10, nsnMax: 10, example: '876 123 4567' },
  { iso2: 'TT', name: 'Trinidad and Tobago', dialCode: '+1', nsnMin: 10, nsnMax: 10, example: '868 123 4567' },
  { iso2: 'FJ', name: 'Fiji', dialCode: '+679', nsnMin: 7, nsnMax: 7, example: '701 2345' },
  { iso2: 'PG', name: 'Papua New Guinea', dialCode: '+675', nsnMin: 8, nsnMax: 8, example: '7012 3456' },
];

export const getCountryByIso2 = (iso2) =>
  COUNTRY_CODES.find((c) => c.iso2 === iso2) || COUNTRY_CODES[0];

/**
 * Preferred-language → default country. Matches the site's currently
 * registered language codes; unknown languages fall back to the caller's
 * chosen default (usually IP-region or the site's home country).
 */
export const LANGUAGE_TO_ISO2 = {
  en: 'GB',
  ja: 'JP',
  ko: 'KR',
  vi: 'VN',
  zh: 'CN',
  es: 'ES',
  fr: 'FR',
  de: 'DE',
  pt: 'PT',
  it: 'IT',
  ru: 'RU',
  th: 'TH',
  id: 'ID',
  ar: 'SA',
  hi: 'IN',
  tr: 'TR',
};
